
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type MobileMenuContextType = {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
};

const MobileMenuContext = createContext<MobileMenuContextType | undefined>(undefined);

export const MobileMenuProvider = ({ children }: { children: ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Fechar menu ao mudar de rota
  useEffect(() => {
    closeMenu();
  }, [window.location.pathname]);

  // Evitar scroll quando o menu estiver aberto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Fechar o menu quando clicar fora
  useEffect(() => {
    if (!isMenuOpen) return;
    
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Verificar se o clique foi no backdrop
      if (target.classList.contains('mobile-menu-backdrop')) {
        closeMenu();
        return;
      }
      
      // Ignorar cliques no botão de toggle ou no próprio menu
      if (target.closest('.mobile-menu') || 
          target.closest('button[aria-controls="mobile-menu"]') ||
          target.closest('.MobileMenuToggle')) {
        return;
      }
      
      closeMenu();
    };

    document.addEventListener('click', handleOutsideClick);
    
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isMenuOpen]);

  return (
    <MobileMenuContext.Provider value={{ isMenuOpen, toggleMenu, closeMenu }}>
      {children}
    </MobileMenuContext.Provider>
  );
};

export const useMobileMenu = () => {
  const context = useContext(MobileMenuContext);
  
  if (context === undefined) {
    throw new Error("useMobileMenu must be used within a MobileMenuProvider");
  }
  
  return context;
};
